import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  COOKIE_CONSENT,
  COOKIE_CONSENT_EVENT,
  getCookieConsent,
  setCookieConsent,
} from "@/lib/cookieConsent";
import { isNativeApp } from "@/lib/runtime";
import { useAuth } from "@/contexts/AuthContext";
import { useComplianceStatus, usePrivacyPreferences } from "@/queries/account";

export default function CookieBanner() {
  const location = useLocation();
  const native = isNativeApp();
  const { user } = useAuth();
  const { data: complianceStatus, isLoading: complianceLoading } = useComplianceStatus();
  const { data: privacyPreferences, isLoading: privacyLoading } = usePrivacyPreferences();
  const nativePublicRoute = native && (
    location.pathname.startsWith("/auth") ||
    location.pathname === "/legal" ||
    location.pathname === "/om-lapstr"
  );
  const [choice, setChoice] = useState(() => getCookieConsent());

  useEffect(() => {
    const sync = () => setChoice(getCookieConsent());
    window.addEventListener(COOKIE_CONSENT_EVENT, sync);
    return () => window.removeEventListener(COOKIE_CONSENT_EVENT, sync);
  }, []);

  useEffect(() => {
    setChoice(getCookieConsent());
  }, [user?.id]);

  const isLegalCookiesPage = useMemo(() => {
    return location.pathname === "/legal" && location.hash === "#cookies";
  }, [location.hash, location.pathname]);

  if (
    choice ||
    isLegalCookiesPage ||
    !user?.id ||
    complianceLoading ||
    privacyLoading ||
    privacyPreferences?.analytics_decided === true ||
    !complianceStatus?.complete ||
    !complianceStatus?.is_adult
  ) return null;

  return (
    <div
      className="native-v3-cookie-banner fixed inset-x-0 bottom-[max(env(safe-area-inset-bottom),4.75rem)] z-40 px-3 sm:bottom-4 sm:px-4"
      data-public-route={nativePublicRoute ? "true" : "false"}
    >
      <div className="native-v3-cookie-panel mx-auto max-w-3xl rounded-2xl border border-border bg-background/95 p-4 shadow-2xl backdrop-blur">
        <div className="native-v3-cookie-layout flex items-start gap-3">
          <div className="native-v3-cookie-icon mt-0.5 rounded-full bg-muted p-2">
            <Cookie className="h-4 w-4 text-muted-foreground" />
          </div>

          <div className="native-v3-cookie-content min-w-0 flex-1 space-y-3">
            <div className="space-y-1">
              <span className="native-v3-cookie-kicker">DIN INTEGRITET</span>
              <p className="text-sm font-semibold text-foreground">
                {native ? "Hjälp oss förbättra Lapstr" : "Cookies och användningsstatistik"}
              </p>
              <p className="text-sm leading-relaxed text-muted-foreground">
                {native
                  ? "Samtyck till frivillig användningsstatistik som hjälper Lapstr att utveckla appens funktioner och stabilitet. Valet kan ändras när som helst."
                  : "Nödvändig lagring används för inloggning och säkerhet. Med ditt samtycke behandlar Lapstr även användningsstatistik i syfte att utveckla tjänstens funktioner och stabilitet."}{" "}
                <Link className="text-primary underline-offset-4 hover:underline" to="/legal#cookies">
                  Läs mer
                </Link>
                .
              </p>
            </div>

            <div className="native-v3-cookie-actions flex flex-col gap-2 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setCookieConsent(
                    COOKIE_CONSENT.NECESSARY,
                    native ? "native_prompt" : "web_banner"
                  );
                  setChoice(COOKIE_CONSENT.NECESSARY);
                }}
              >
                {native ? "Nej tack" : "Endast nödvändiga"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setCookieConsent(
                    COOKIE_CONSENT.ALL,
                    native ? "native_prompt" : "web_banner"
                  );
                  setChoice(COOKIE_CONSENT.ALL);
                }}
              >
                Tillåt statistik
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
